import { gql } from '@urql/core'
import { Job } from 'entities/job'
import {
    FetchedCompanyForUpsertJobForm,
    FetchedJobForUpsertJobForm,
} from 'containers/upsert-job/types/fetchedEntities'
import {
    CreateJobMutationInput,
    UpdateCompanyInput,
    UpdateJobMutationInput,
} from 'containers/upsert-job/types/mutationInputs'
import { AffiliateFragment } from 'components/job/queries'

export const SHARED_JOB_FIELDS_TO_INVALIDATE_CACHE = gql`
    fragment SharedJobFields on Job {
        id
        published
        slug
        title
        tagline
        yearsOfCommercialExperience
        formalEducation
        spokenLanguages {
            name
            level
        }
        tags {
            id
            slug
            label
        }
        techExperienceRequirements {
            id
            level
        }
        descriptionHtml
        manager
        approved
        archived
        remuneration {
            competitive
            min
            max
            currency
            timePeriod
            equity
        }
        remote
        location {
            street
            city
            country
            countryCode
            state
            postCode
            longitude
            latitude
        }
        verticals
        sponsorshipOffered
        atsJobId
        atsJobName
        roleType
        remoteInfo {
            regionRestrictions
            timezoneRestrictions {
                timezoneName
                timezoneDelta {
                    plus
                    minus
                }
            }
        }
        companyId
        lastModified
    }
`

const DetailedCompanyFragment = gql`
    fragment DetailedCompanyFragment on Company {
        id
        name
        slug
        logo
        description
        descriptionHtml
        tags {
            id
            type
            label
        }
        locations {
            city
            country
        }
        profileEnabled
        totalPublishedJobCount
        pendingOffer {
            recurringFee
        }
        offer {
            recurringFee
        }
        permissions
    }
`

export const SHARED_QUERY_JOB = gql<
    { job: FetchedJobForUpsertJobForm },
    { id?: string } | { slug?: string }
>`
    query job($id: ID, $slug: String) {
        job(id: $id, slug: $slug) {
            ...SharedJobFields
            company {
                ...DetailedCompanyFragment
            }
            affiliate {
                ...AffiliateFragment
            }
        }
    }
    ${SHARED_JOB_FIELDS_TO_INVALIDATE_CACHE}
    ${DetailedCompanyFragment}
    ${AffiliateFragment}
`

//

export const QUERY_COMPANY = gql<
    { company: FetchedCompanyForUpsertJobForm },
    { id: string }
>`
    query company($id: ID) {
        company(id: $id) {
            id
            descriptionHtml
            logo
            name
            tags {
                id
                label
                type
                slug
            }
            integrations {
                greenhouse {
                    enabled
                }
                workable {
                    enabled
                }
            }
        }
    }
`

export const MUTATION_CREATE_JOB = gql<
    { create_job: Pick<Job, 'id' | 'slug'> },
    { create_job: CreateJobMutationInput }
>`
    mutation create_job($create_job: CreateJobInput!) {
        create_job(create_job: $create_job) {
            id
            slug
        }
    }
`

export const MUTATION_UPDATE_JOB = gql<
    { update_job: Pick<Job, 'id' | 'slug'> },
    { update_job: UpdateJobMutationInput }
>`
    mutation update_job($update_job: UpdateJobInput!) {
        update_job(update_job: $update_job) {
            ...SharedJobFields
        }
    }
    ${SHARED_JOB_FIELDS_TO_INVALIDATE_CACHE}
`

export const MUTATION_UPDATE_COMPANY = gql<never, { update_company: UpdateCompanyInput }>`
    mutation update_company($update_company: UpdateCompanyInput!) {
        update_company(update_company: $update_company) {
            id
        }
    }
`
